import { useState, useEffect, useCallback, useMemo } from 'react';
import { userFactsService } from '../services/userFactsService';
import { UserFactsDashboardResponse } from '../types/userFacts';
import { useAuth } from '../context/AuthContext';
import { subDays } from 'date-fns';
import { formatKeyLabel, getMetricConfig } from '../utils/wellbeingDataProcessor';

export type PeriodOption = '7d' | '30d' | '90d' | '180d' | '1y';

export interface PeriodConfig {
  label: string;
  shortLabel: string;
  days: number;
}

export const PERIOD_OPTIONS: Record<PeriodOption, PeriodConfig> = {
  '7d': { label: 'Last 7 days', shortLabel: '7D', days: 7 },
  '30d': { label: 'Last 30 days', shortLabel: '1M', days: 30 },
  '90d': { label: 'Last 3 months', shortLabel: '3M', days: 90 },
  '180d': { label: 'Last 6 months', shortLabel: '6M', days: 180 },
  '1y': { label: 'Last year', shortLabel: '1Y', days: 365 },
};

export interface ProcessedNumericFact {
  key: string;
  displayName: string;
  currentValue: number;
  averageValue: number;
  history: Array<{
    date: string;
    value: number;
    rawValue: string;
    version: number;
  }>;
  trend: 'up' | 'down' | 'stable';
  changePercent: number;
  trendLabel: string;
}

export interface ProcessedStringFact {
  key: string;
  displayName: string;
  currentValue: string;
  lastUpdated: string;
  version: number;
}

export interface UseUserFactsDashboardReturn {
  // Data
  numericFacts: ProcessedNumericFact[];
  stringFacts: ProcessedStringFact[];
  summary: {
    numericFactCount: number;
    stringFactCount: number;
    totalChangesInPeriod: number;
  } | null;
  
  // State
  loading: boolean;
  error: string | null;
  period: PeriodOption;
  
  // Actions
  setPeriod: (period: PeriodOption) => void;
  refreshDashboard: () => Promise<void>;
  clearError: () => void;
}

function calculateTrend(history: Array<{ value: number }>): { trend: 'up' | 'down' | 'stable'; changePercent: number } {
  if (history.length < 2) {
    return { trend: 'stable', changePercent: 0 };
  }
  
  const firstValue = history[0].value;
  const lastValue = history[history.length - 1].value;
  
  if (firstValue === 0) {
    return { trend: lastValue > 0 ? 'up' : 'stable', changePercent: 0 };
  }
  
  const changePercent = ((lastValue - firstValue) / firstValue) * 100;
  
  if (Math.abs(changePercent) < 5) {
    return { trend: 'stable', changePercent };
  }
  
  return {
    trend: changePercent > 0 ? 'up' : 'down',
    changePercent
  };
}

function calculateAverage(history: Array<{ value: number }>): number {
  if (history.length === 0) return 0;
  const sum = history.reduce((acc, h) => acc + h.value, 0);
  return Math.round((sum / history.length) * 10) / 10;
}

/**
 * Get a short descriptive trend label.
 * For inverted-scale metrics (e.g. stress), the sentiment is flipped:
 *   value going up → negative, value going down → positive.
 */
function getTrendLabel(
  trend: 'up' | 'down' | 'stable',
  changePercent: number,
  invertedScale: boolean
): string {
  if (trend === 'stable') return 'Steady';

  const magnitude = Math.abs(changePercent);
  const isLargeChange = magnitude >= 20;

  if (invertedScale) {
    // Stress-like metrics: describe the metric itself so arrow + word align
    // up arrow + "Increasing" (bad), down arrow + "Easing" (good)
    if (trend === 'up') {
      return isLargeChange ? 'Spiking' : 'Increasing';
    } else {
      return isLargeChange ? 'Dropping fast' : 'Easing';
    }
  }

  // Normal scale metrics
  if (trend === 'up') {
    return isLargeChange ? 'Strong rise' : 'Improving';
  } else {
    return isLargeChange ? 'Sharp drop' : 'Declining';
  }
}

export const useUserFactsDashboard = (): UseUserFactsDashboardReturn => {
  const { isAuthenticated } = useAuth();
  const [dashboardData, setDashboardData] = useState<UserFactsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodOption>('30d');

  const loadDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const periodConfig = PERIOD_OPTIONS[period];
      const sinceDate = subDays(new Date(), periodConfig.days);
      const since = sinceDate.toISOString();

      const response = await userFactsService.getDashboard(since, 500);
      setDashboardData(response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(errorMessage);
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, period]);

  // Process numeric facts for display
  const numericFacts = useMemo((): ProcessedNumericFact[] => {
    if (!dashboardData?.numericFacts) return [];

    return Object.entries(dashboardData.numericFacts).map(([key, data]) => {
      const sortedHistory = [...data.history].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      const processedHistory = sortedHistory.map(point => ({
        date: point.timestamp,
        value: point.value,
        rawValue: point.rawValue,
        version: point.version
      }));

      const { trend, changePercent } = calculateTrend(processedHistory);
      const averageValue = calculateAverage(processedHistory);
      const metricConfig = getMetricConfig(key);
      const invertedScale = metricConfig?.invertedScale ?? false;
      const trendLabel = getTrendLabel(trend, changePercent, invertedScale);

      return {
        key,
        displayName: formatKeyLabel(key),
        currentValue: data.currentValue,
        averageValue,
        history: processedHistory,
        trend,
        changePercent,
        trendLabel
      };
    }).sort((a, b) => b.history.length - a.history.length); // Sort by most data points
  }, [dashboardData]);

  // Process string facts for display
  const stringFacts = useMemo((): ProcessedStringFact[] => {
    if (!dashboardData?.stringFacts) return [];

    return Object.entries(dashboardData.stringFacts).map(([key, data]) => ({
      key,
      displayName: formatKeyLabel(key),
      currentValue: data.currentValue,
      lastUpdated: data.lastUpdated,
      version: data.version
    })).sort((a, b) => 
      new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
    ); // Sort by most recently updated
  }, [dashboardData]);

  const summary = useMemo(() => {
    if (!dashboardData?.summary) return null;
    return dashboardData.summary;
  }, [dashboardData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshDashboard = useCallback(async () => {
    await loadDashboard();
  }, [loadDashboard]);

  // Load dashboard on mount and when period changes
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboard();
    }
  }, [isAuthenticated, loadDashboard]);

  return {
    numericFacts,
    stringFacts,
    summary,
    loading,
    error,
    period,
    setPeriod,
    refreshDashboard,
    clearError
  };
};
